/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Decimal } from 'decimal.js';

import {
  Money,
  MoneyAmountData,
} from '@/lib/money';

import {
  Address,
  StoreAddress,
} from './address';
import { DraftOrder } from './draftOrder';
import {
  DraftOrderItemData,
  DraftOrderItem,
} from './draftOrderItem';
import {
  FulfillmentService,
  FulfillmentServiceData,
} from './fulfillmentService';
import {
  FulfillmentPrice,
  FulfillmentPriceData,
} from './fulfillmentPrice';

export interface DraftFulfillmentOptionData {
  service: FulfillmentServiceData;

  // Customer addresses that were stored for the fulfillment; Only available for fulfillments that
  // require shipping.
  addresses?: Address[] | StoreAddress[];

  // The shipping price if the fulfillment is selected for the item. This is for display purpose.
  shippingPrice?: MoneyAmountData;
}

export interface DraftFulfillmentData {
  // The service that is selected to fulfill the fulfillment
  service: FulfillmentServiceData;

  // Items in the draft order that are fulfilled by this fulfillment
  items: DraftOrderItemData[];

  // The shipping address selected for the delivery; It may not be always available. For example,
  // when the fulfillment is via in-store pickup, there's no shipping address by definition.
  shippingAddress?: Address | StoreAddress;

  // The fulfillment fee
  price: FulfillmentPriceData;

  // (Extension) List of all available addresses for delivery
  shippingAddressOptions?: Address[] | StoreAddress[];

  // (Extension) List of all available fulfillments that can deliver the items in this fulfillment
  fulfillmentOptions?: DraftFulfillmentOptionData[];
}

export class DraftFulfillment {
  public readonly service: FulfillmentService;
  public readonly price: FulfillmentPrice;
  public readonly items: DraftOrderItem[];
  public readonly fulfillmentOptions?: Array<{
    service: FulfillmentService;
    addresses: Address[];
    shippingPrice?: Money;
  }>;

  constructor(
    currency: string,
    // order that contains this fulfillment.
    order: DraftOrder,
    private data: DraftFulfillmentData,
  ) {
    this.service = FulfillmentService.create(data.service);
    this.price = new FulfillmentPrice(currency, data.price);

    this.items = this.data.items.map((itemData) => {
      const item = order.items.find((i) => (i.id === itemData.id));
      if (!item) {
        throw new Error(`Fulfillment contains item that is not found in the order: ${itemData.id}`);
      }
      return item;
    });

    if (data.fulfillmentOptions) {
      this.fulfillmentOptions = data.fulfillmentOptions.map((fulfillment) => ({
        service: FulfillmentService.create(fulfillment.service),
        addresses: (fulfillment.addresses) ? fulfillment.addresses : [],
        shippingPrice: (fulfillment.shippingPrice) ? {
          amount: new Decimal(fulfillment.shippingPrice),
          currency,
        } : undefined,
      }));
    }
  }

  get shippingAddress() { return this.data.shippingAddress; }
}
